import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Bell, CheckCircle2, BookOpen, Gift, Award, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_ICONS = {
  enrollment: BookOpen,
  quiz_result: Award,
  referral: Gift,
};

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const fetchNotifs = async () => {
    try {
      const { data } = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(data.notifications || []);
      setUnread(data.unread_count || 0);
    } catch {}
  };

  useEffect(() => {
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const markRead = async () => {
    try {
      await axios.post(`${API}/notifications/read`, {}, { withCredentials: true });
      setUnread(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch {}
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => { setOpen(!open); if (!open && unread > 0) markRead(); }}
        data-testid="notification-bell"
        className="relative text-zinc-400 hover:text-white transition-colors p-1"
      >
        <Bell className="w-5 h-5" strokeWidth={1.5} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#CC0000] text-white text-[10px] font-bold flex items-center justify-center">
            {unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notification-panel" className="absolute right-0 top-10 w-80 bg-[#111111] border border-[#262626] shadow-2xl z-50 max-h-96 overflow-y-auto">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#262626]">
            <p className="text-sm font-semibold text-white">Notifications</p>
            <button onClick={() => setOpen(false)} className="text-zinc-400 hover:text-white">
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
          {notifications.length === 0 ? (
            <div className="p-6 text-center text-zinc-400 text-sm">No notifications yet</div>
          ) : (
            notifications.slice(0, 20).map((n, i) => {
              const Icon = TYPE_ICONS[n.type] || Bell;
              return (
                <div key={i} data-testid={`notif-${i}`} className={`flex gap-3 p-4 border-b border-[#262626] last:border-b-0 ${!n.read ? 'bg-[#0077B6]/5' : ''}`}>
                  <Icon className="w-5 h-5 text-[#0077B6] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div>
                    <p className="text-sm font-medium text-white">{n.title}</p>
                    <p className="text-xs text-zinc-400 mt-0.5">{n.message}</p>
                    <p className="text-xs text-zinc-500 mt-1">{new Date(n.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
